import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle, Clock, FileText, Tag, Video } from 'lucide-react';
import React from 'react';
import ImageWithPlaceholder from '../../../shared/components/ImageWithPlaceholderProps';
import { Service } from '../../../shared/hooks/useServiceSelection';

// Define default colors to use if not provided
const defaultColors = {
  cardBackground: "#000000F2",
        cardBorder: "#DDB88919",
        cardBorderSelected: "#22c55e49",
        cardShadow: "rgba(221,184,137,0.07)",
        cardShadowSelected: "rgba(34,197,94,0.25)",
        titleText: "#DDB889",
        subtitleText: "#9CA3AF",
        infoText: "#DDB889",
        iconColor: "#DDB889",
        selectionBackground: "#22c55e",
        selectionIcon: "#FFFFFF",
        badgeBackground: "#000000F2",
        badgeBorder: "#DDB88919",
        buttonBackground: "#DDB889",
        buttonText: "#000000",
        placeholderGradientFrom: "#DDB88933",
        placeholderGradientTo: "#DDB8890D",
        placeholderIconBackground: "#DDB88933"
};

interface ServiceCardProps {
  service: Service;
  isSelected: boolean;
  currency: { symbol: string };
  onToggle: (service: Service) => void;
  formatDuration: (minutes: number) => string;
  shouldShowReadMore: (text: string) => boolean;
  onReadMore: (id: string) => void;
  colors?: Partial<typeof defaultColors>;
  showPrices?: boolean;
  showDuration?: boolean;
  showDescriptions?: boolean;
  showImages?: boolean;
}

export const ServiceCard: React.FC<ServiceCardProps> = ({
  service,
  isSelected,
  currency,
  onToggle,
  formatDuration,
  shouldShowReadMore,
  onReadMore,
  colors = {},
  showPrices = true,
  showDuration = true,
  showDescriptions = true,
  showImages = true
}) => {
  // Merge provided colors with defaults
  const themeColors = { ...defaultColors, ...colors };

  // Get shadow style based on selection state
  const getShadowStyle = () => {
    return isSelected
      ? `0 0 15px ${themeColors.cardShadowSelected}`
      : `0 0 15px ${themeColors.cardShadow}`;
  };

  // Get hover shadow style
  const getHoverShadowStyle = () => {
    return `0 0 20px ${themeColors.cardShadowSelected}`;
  };

  // Get border color based on selection state
  const getBorderColor = () => {
    return isSelected ? themeColors.cardBorderSelected : themeColors.cardBorder;
  };

  return (
    <motion.div
      className="lp-relative"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        className="lp-group lp-relative lp-overflow-hidden lp-backdrop-blur-sm
          lp-border lp-rounded-2xl lp-cursor-pointer
          lp-transition-all lp-duration-700 lp-h-full lp-flex lp-flex-col"
        style={{
          backgroundColor: themeColors.cardBackground,
          borderColor: getBorderColor(),
          boxShadow: getShadowStyle()
        }}
        onClick={() => onToggle(service)}
        whileHover={{
          boxShadow: getHoverShadowStyle()
        }}
      >
        {/* Image Section - Only shown if showImages is true */}
        {showImages && (
          <div className="lp-relative lp-h-48 lp-overflow-hidden">
            <ImageWithPlaceholder
              image={service.image}
              alt={service.name}
              className="lp-w-full lp-h-full lp-object-cover"
              placeholderColors={{
                from: themeColors.placeholderGradientFrom,
                to: themeColors.placeholderGradientTo,
                iconBackground: themeColors.placeholderIconBackground
              }}
            />

            {/* Overlay Gradient - On top of image but under badges */}
            <div className="lp-absolute lp-inset-0 lp-z-[1] lp-pointer-events-none lp-bg-gradient-to-t lp-from-black/60 lp-via-black/20 lp-to-transparent group-hover:lp-from-black/70 group-hover:lp-via-black/30 lp-transition-all lp-duration-300" />

            {/* Info Badges - Always on top */}
            <div className="lp-absolute lp-bottom-4 lp-left-4 lp-right-4 lp-flex lp-justify-between lp-items-center lp-z-[2]">
              {showPrices && (
                <div className="lp-backdrop-blur-sm lp-px-4 lp-py-2 lp-rounded-xl lp-border"
                  style={{
                    backgroundColor: themeColors.badgeBackground,
                    borderColor: themeColors.badgeBorder,
                    color: themeColors.infoText
                  }}
                >
                  <span className="lp-font-medium" style={{ color: themeColors.infoText }}>
                    {currency?.symbol}
                    {service.price}
                  </span>
                </div>
              )}

              {/* ✨ Video Service Badge */}
    {service.isVideoService && (
      <div className="lp-backdrop-blur-sm lp-px-4 lp-py-2 lp-rounded-xl lp-border lp-flex lp-items-center lp-gap-2"
        style={{
          backgroundColor: themeColors.badgeBackground,
          borderColor: themeColors.badgeBorder
        }}
      >
        <Video className="lp-w-4 lp-h-4" style={{ color: themeColors.iconColor }} />
        <span style={{ color: themeColors.infoText }}>Online</span>
      </div>
    )}
              {showDuration && (
                <div className="lp-backdrop-blur-sm lp-px-4 lp-py-2 lp-rounded-xl lp-border lp-flex lp-items-center lp-gap-2"
                  style={{
                    backgroundColor: themeColors.badgeBackground,
                    borderColor: themeColors.badgeBorder
                  }}
                >
                  <Clock className="lp-w-4 lp-h-4" style={{ color: themeColors.iconColor }} />
                  <span style={{ color: themeColors.infoText }}>
                    {formatDuration(service.duration)}
                  </span>
                </div>
              )}
            </div>

            {/* Selection Badge - Always on top */}
            <AnimatePresence>
              {isSelected && (
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  className="lp-absolute lp-top-4 lp-right-4 lp-w-8 lp-h-8 lp-rounded-full lp-flex lp-items-center lp-justify-center lp-z-[2]"
                  style={{ backgroundColor: themeColors.selectionBackground }}
                >
                  <CheckCircle className="lp-w-5 lp-h-5" style={{ color: themeColors.selectionIcon }} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Content Section */}
        <div className="lp-p-6 lp-flex lp-flex-col lp-flex-1 lp-min-h-[142px] lp-relative">
          <div className="lp-flex lp-gap-2 lp-items-start">
            <Tag className="lp-w-5 lp-h-5 lp-shrink-0 lp-mt-1" style={{ color: themeColors.iconColor }} />
            <h3
              className="lp-text-xl lp-font-medium lp-mb-4 lp-overflow-hidden lp-text-ellipsis lp-flex-1"
              style={{
                color: themeColors.titleText,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {service.name}
            </h3>
          </div>

          {/* Only show descriptions if showDescriptions is true */}
          {showDescriptions && (
            <div className="lp-flex lp-flex-col lp-flex-1">
              <div className="lp-flex lp-gap-2 lp-items-start">
                <FileText className="lp-w-5 lp-h-5 lp-shrink-0 lp-mt-1" style={{ color: themeColors.iconColor }} />
                <p
                  className="lp-text-sm lp-leading-relaxed lp-overflow-hidden lp-text-ellipsis lp-mb-2 lp-flex-1"
                  style={{
                    color: themeColors.subtitleText,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {service.description}
                </p>
              </div>
              {shouldShowReadMore(service.description) && (
                <motion.button
                  onClick={(e) => {
                    e.stopPropagation();
                    onReadMore(service.id);
                  }}
                  className="lp-text-sm lp-mt-auto lp-self-start lp-transition-colors lp-duration-300"
                  style={{ color: themeColors.buttonText }}
                  whileHover={{
                    scale: 1.05,
                    //@ts-expect-error
                    color: themeColors.buttonHoverText || themeColors.buttonText
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  Read More
                </motion.button>
              )}
            </div>
          )}

          {/* If we're not showing images, show the selection indicator here */}
          {!showImages && isSelected && (
            <div className="lp-absolute lp-top-4 lp-right-4">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="lp-w-8 lp-h-8 lp-rounded-full lp-flex lp-items-center lp-justify-center"
                style={{ backgroundColor: themeColors.selectionBackground }}
              >
                <CheckCircle className="lp-w-5 lp-h-5" style={{ color: themeColors.selectionIcon }} />
              </motion.div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ServiceCard;
